import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readContentControls } from "@/office/word/formulare";

export const lektionEntwicklertools: Lesson = {
  id: "entwicklertools",
  title: "Die Entwicklertools einblenden",
  summary: "Die versteckte Registerkarte, ohne die keine Formulare gehen.",
  durationMinutes: 7,
  learningGoals: [
    "Die Registerkarte „Entwicklertools“ aktivieren",
    "Inhaltssteuerelemente von normalem Text unterscheiden",
    "Ein erstes Texteingabefeld anlegen",
  ],
  steps: [
    {
      id: "registerkarte",
      title: "Entwicklertools aktivieren",
      explanation: [
        "Formulare in Word bestehen aus Inhaltssteuerelementen: ausfüllbare Felder, " +
          "Kontrollkästchen, Auswahllisten, Datumsauswahl. Die Werkzeuge dafür liegen auf " +
          "der Registerkarte „Entwicklertools“ – und die ist standardmäßig ausgeblendet.",
        "Einblenden lässt sie sich über „Datei“ → „Optionen“ → „Menüband anpassen“. In " +
          "der rechten Liste setzt du den Haken bei „Entwicklertools“ und bestätigst. Die " +
          "Registerkarte erscheint dann dauerhaft, auch in anderen Dokumenten.",
        "Der Unterschied zu einem selbst gebauten Formular mit Unterstrichen ist " +
          "erheblich: Ein Inhaltssteuerelement lässt sich gezielt anspringen, kann die " +
          "Eingabe einschränken, behält seine Beschriftung und lässt sich später " +
          "maschinell auslesen. Unterstriche in einer Zeile können das alles nicht.",
      ],
      keyPoints: [
        "Datei → Optionen → Menüband anpassen → Entwicklertools.",
        "Die Einstellung gilt dauerhaft für alle Dokumente.",
        "Steuerelemente sind mehr als eine Linie zum Draufschreiben.",
      ],
      task: {
        instruction:
          "Blende die Entwicklertools ein und füge ein Inhaltssteuerelement für " +
          "Rich-Text oder Nur-Text in dein Dokument ein.",
        hint: "Die ersten beiden Symbole in der Gruppe „Steuerelemente“ sind Rich-Text und Nur-Text.",
        check: async () => {
          const felder = await readContentControls();
          if (felder.length === 0) {
            return fail(
              "Es wurde noch kein Inhaltssteuerelement gefunden.",
              "Die Registerkarte „Entwicklertools“ muss eingeblendet sein – erst dort findest du die Steuerelemente.",
            );
          }
          return pass(
            `${felder.length} ${felder.length === 1 ? "Steuerelement" : "Steuerelemente"} gefunden (${felder[0].type}).`,
          );
        },
      },
    },
    {
      id: "titel-und-tag",
      title: "Titel und Tag vergeben",
      explanation: [
        "Jedes Steuerelement hat zwei Kennungen, die man leicht übersieht. Über " +
          "„Eigenschaften“ in der Gruppe „Steuerelemente“ erreichst du beide.",
        "Der „Titel“ ist die Beschriftung, die Word über dem Element anzeigt, wenn es " +
          "angeklickt wird – sichtbar für die ausfüllende Person und damit eine " +
          "Ausfüllhilfe.",
        "Das „Tag“ ist eine unsichtbare Kennung. Sie ist entscheidend, sobald ein " +
          "Formular ausgewertet werden soll: Ein Programm findet das Feld „Nachname“ " +
          "über sein Tag, unabhängig davon, wo im Dokument es steht oder wie die " +
          "Beschriftung lautet.",
        "In einem Formular, das nur ausgedruckt wird, kann man auf das Tag verzichten. " +
          "Sobald die Daten weiterverarbeitet werden, ist es das Wichtigste am ganzen " +
          "Element.",
      ],
      keyPoints: [
        "Entwicklertools → Eigenschaften öffnet Titel und Tag.",
        "Titel: sichtbare Beschriftung. Tag: unsichtbare Kennung zur Auswertung.",
        "Tags eindeutig vergeben – sie sind der Schlüssel zu den Daten.",
      ],
      task: {
        instruction:
          "Gib deinem Steuerelement über die Eigenschaften einen Titel und ein Tag.",
        hint: "Element anklicken, dann Entwicklertools → Eigenschaften.",
        check: async () => {
          const felder = await readContentControls();
          if (felder.length === 0) {
            return fail(
              "Es wurde noch kein Inhaltssteuerelement gefunden.",
              "Füge zuerst wie im vorherigen Schritt ein Steuerelement ein.",
            );
          }

          const mitBeidem = felder.find(
            (feld) => feld.title.length > 0 && feld.tag.length > 0,
          );
          if (!mitBeidem) {
            const mitTitel = felder.filter((feld) => feld.title.length > 0).length;
            const mitTag = felder.filter((feld) => feld.tag.length > 0).length;
            return fail(
              `Noch kein Element hat beides (mit Titel: ${mitTitel}, mit Tag: ${mitTag}).`,
              "Beide Felder stehen im selben Dialog ganz oben.",
            );
          }
          return pass(`„${mitBeidem.title}“ ist gesetzt, das Tag lautet „${mitBeidem.tag}“.`);
        },
      },
    },
  ],
};
